import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonIcon,
  Icon,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { NODE_DATA_MODEL } from '../../../models/CertsModels';
import { SET_IS_REASSIGN_POPOVER_MSG } from '../../../redux/certdetails/types';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_APPLICATION_OWNER,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  CAMPAIGNTYPE_SERVICEACCOUNT,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey } from '../../../utils/utils';
import { CERT_TILE_TYPE } from '../../CertsDetailsList/Tile/constant';
import CertsModalCommentBox from '../../CertsModalCommentBox';
import { TAB_STATUS_DONE } from '../../ViewControls';
import CertsActionPopover from '../CertsActionPopover/CertsActionPopover';
import CertsConsultContent from '../CertsActionPopover/CertsConsultContent';
import CertsConsultedActionPopover from '../CertsActionPopover/CertsConsultedActionPopover';
import CertsConsultedContent from '../CertsActionPopover/CertsConsultedContent';
import CertsConsultPopover from '../CertsActionPopover/CertsConsultPopover';
import CertsRejectAction from '../CertsRejectAction';
import {
  ACTION_BUTTON_KINDS,
  PREDEFINED_COMMENTS_FOR_ACTION,
  REJECT_ACTION_DATA,
} from '../constant';
import { getCommentSubmitLabel } from '../helpers/getCommentSubmitLabel';

import { invertedMessages } from './messages';

import './CertsSidePanelCommentDrawer.css';

export const DRAWER_CLOSE_TRANSITION_DELAY_MS = 10;
export const DRAWER_SPEED_OF_CLOSE_TRANSITION_MS = 220;

function CertsSidePanelCommentDrawer({
  isSidePanelCommentDrawerOpen,
  setIsSidePanelCommentDrawerOpen,
  drawerActionKind,
  onSubmit,
  getTypeActEntRole,
  isRejectCommentRequired,
  setIsSidePanelLoadingKind,
  nodeData,
  globalDateFormat,
  certKey,
  drawerActionSelected,
  consulteeLoggedIn,
  isSaviResponded,
  closeConsultPopover,
  actionBtnRefs,
  tooltipPosition,
  isMobileXlargeWidth,
  activeTab,
  isActionDrawerOutsideClick,
  setIsActionDrawerOutsideClick,
}) {
  // Form
  const [isCritical, setIsCritical] = useState(false);
  const [commentValue, setCommentValue] = useState('');
  const [isConsultPopoverShown, setIsConsultPopoverShown] = useState(false);
  // Transitions
  const [dropdownOpenHeight, setDropdownOpenHeight] = useState(0);
  const drawerRef = useRef(null);
  const contentRef = useRef(null);
  const { messages } = useSelector((state) => state?.user);

  const { predefinedComments } = useSelector(
    (state) => state?.certifications?.details?.core
  );

  const dispatch = useDispatch();
  const {
    core: { campaignType },
  } = useSelector((state) => state?.certifications?.details);
  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  const isAOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_APPLICATION_OWNER;
  const isSACampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT;

  const { actionTakenByPrimaryCertifier, isConsulted } = nodeData;

  const isWorksForMe = drawerActionKind === ACTION_BUTTON_KINDS.WORKS_FOR_ME;
  const isBelongsToMe = drawerActionKind === ACTION_BUTTON_KINDS.BELONGS_TO_ME;
  const isApprove = drawerActionKind === ACTION_BUTTON_KINDS.APPROVE;
  const isReject = drawerActionKind === ACTION_BUTTON_KINDS.REJECT;
  const isTerminated = drawerActionKind === ACTION_BUTTON_KINDS.TERMINATE;
  const isDecommission = drawerActionKind === ACTION_BUTTON_KINDS.DECOMMISSION;
  const isReassignUserOrParent =
    drawerActionKind === ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME ||
    drawerActionKind === ACTION_BUTTON_KINDS.DOES_NOT_BELONG_TO_ME ||
    (isAOCampaign &&
      getTypeActEntRole === CERT_TILE_TYPE.ACCOUNT &&
      drawerActionKind === ACTION_BUTTON_KINDS.REASSIGN);
  const showConsultedLineItemMsg =
    isConsulted &&
    !consulteeLoggedIn &&
    !isConsultPopoverShown &&
    drawerActionKind !== ACTION_BUTTON_KINDS.CONSULT &&
    drawerActionKind !== ACTION_BUTTON_KINDS.EXTEND_END_DATE &&
    !isSaviResponded &&
    activeTab !== TAB_STATUS_DONE;
  const showConsultedActionedInfo =
    !actionTakenByPrimaryCertifier &&
    drawerActionSelected &&
    drawerActionKind !== ACTION_BUTTON_KINDS.CONSULT; // && isSelected) need to check
  const isCommentNotValid = Boolean(
    isCritical || commentValue?.replace(/[<>]/g, '')?.trim()?.length < 3
  );

  const l = useInvertedLocalize(invertedMessages);
  const intl = useIntl();

  const APPROVE_TITLE_TEXT =
    messages.CERTS_APPROVE || localize(intl, getMessageKey('CERTS_APPROVE'));

  const CONFIRM_MANAGER_TITLE_TEXT =
    messages.CERTS_CONFIRM_MANAGER ||
    localize(intl, getMessageKey('CERTS_CONFIRM_MANAGER'));

  const DECOMMISSION_TEXT =
    messages.CERTS_BULK_DECOMMISSION ||
    localize(intl, getMessageKey('CERTS_BULK_DECOMMISSION'));

  const CANCEL_BUTTON_TEXT =
    messages.CERTS_CANCEL || localize(intl, getMessageKey('CERTS_CANCEL'));

  const sidePanelCommentDrawerClasses = classnames(
    'CertsSidePanelCommentDrawer',
    isSidePanelCommentDrawerOpen && 'CertsSidePanelCommentDrawer--isOpen'
  );

  // Used to get the final height of the container the set it to 0 for the transition animation to play
  useEffect(() => {
    const drawerCurrent = drawerRef.current;
    const drawerContent = contentRef.current;

    if (!drawerCurrent || !drawerContent) return;

    const initialHeight = drawerCurrent.offsetHeight;

    if (isSidePanelCommentDrawerOpen) {
      setDropdownOpenHeight(initialHeight);
      drawerCurrent.style.height = '0px';
      drawerContent.style.overflowY = 'hidden';
    }

    setTimeout(() => {
      drawerContent.style.overflowY = 'auto';
      drawerCurrent.style.height = 'auto';
    }, DRAWER_SPEED_OF_CLOSE_TRANSITION_MS);
  }, [isSidePanelCommentDrawerOpen]);

  // Uses the dropdownOpenHeight from the initial useEffect to set the height of the container for the transition animation to play
  useEffect(() => {
    const drawerCurrent = drawerRef.current;

    if (!drawerCurrent) return;

    if (dropdownOpenHeight > 0)
      setTimeout(() => {
        drawerCurrent.style.height = `${dropdownOpenHeight}px`;
      }, DRAWER_CLOSE_TRANSITION_DELAY_MS);

    if (isSidePanelCommentDrawerOpen && dropdownOpenHeight)
      drawerCurrent.style.opacity = 1;
  }, [dropdownOpenHeight, isSidePanelCommentDrawerOpen]);

  const toggleDrawerOpen = useCallback(() => {
    if (isSidePanelCommentDrawerOpen) {
      const drawerCurrent = drawerRef.current;
      const drawerContent = contentRef.current;

      if (drawerCurrent || drawerContent) {
        const currentHeight = drawerCurrent.offsetHeight;

        drawerCurrent.style.height = `${currentHeight}px`;
        setTimeout(() => {
          drawerCurrent.style.height = '0px';
          drawerContent.style.overflowY = 'hidden';
        }, DRAWER_CLOSE_TRANSITION_DELAY_MS);
        setTimeout(() => {
          drawerCurrent.style.opacity = 0;
          setIsSidePanelCommentDrawerOpen(false);
        }, DRAWER_SPEED_OF_CLOSE_TRANSITION_MS);
      }
    }
  }, [isSidePanelCommentDrawerOpen, setIsSidePanelCommentDrawerOpen]);

  // TODO: @Vishnu, Please add a comment for what this does
  useEffect(() => {
    if (drawerActionKind && actionBtnRefs?.current[drawerActionKind]) {
      if (actionBtnRefs?.current[drawerActionKind]?.current) {
        setIsConsultPopoverShown(
          actionBtnRefs?.current[
            drawerActionKind
          ].current?.getIsConsultPopoverShown()
        );
      }
    }
  }, [actionBtnRefs.current[drawerActionKind], drawerActionKind, nodeData]);

  // Reset form state on close
  useEffect(() => {
    if (!isSidePanelCommentDrawerOpen) {
      setIsCritical(false);
      setCommentValue('');
    }

    // Handle outside click for Action Drawer Ref
    if (
      !isActionDrawerOutsideClick &&
      !isSidePanelCommentDrawerOpen &&
      (isWorksForMe ||
        isBelongsToMe ||
        isApprove ||
        isReject ||
        isDecommission ||
        isTerminated)
    )
      setIsActionDrawerOutsideClick(true);

    // Set false for reject
    if (isActionDrawerOutsideClick && isSidePanelCommentDrawerOpen && isReject)
      setIsActionDrawerOutsideClick(false);
  }, [isSidePanelCommentDrawerOpen]);

  const handleCommentSubmit = () => {
    if (isCommentNotValid) return setIsCritical(true);
    toggleDrawerOpen();
    setIsSidePanelLoadingKind(drawerActionKind);

    return onSubmit(commentValue);
  };

  const shouldGetDrawerCommentForm =
    !showConsultedActionedInfo &&
    !drawerActionSelected &&
    !showConsultedLineItemMsg &&
    (isWorksForMe ||
      isBelongsToMe ||
      isApprove ||
      isDecommission ||
      (isReject && isRejectCommentRequired));

  const getDrawerContent = () => {
    // TODO: Transitions broken, need data to be completely loaded before the drawer actually opens
    if (showConsultedActionedInfo && drawerActionSelected) {
      return (
        <Box ref={contentRef} className='CertsSidePanelCommentDrawer-content'>
          {isMobileXlargeWidth ? (
            <CertsConsultedActionPopover
              isMobileXlargeWidth={isMobileXlargeWidth}
              isCommentPopoverOpen={isSidePanelCommentDrawerOpen}
              setIsCommentPopoverOpen={setIsSidePanelCommentDrawerOpen}
              placement={tooltipPosition}
              onClose={() => {
                if (isSidePanelCommentDrawerOpen)
                  setIsSidePanelCommentDrawerOpen(false);
              }}
              resetAction={onSubmit}
              certKey={certKey}
              nodeData={nodeData}
              globalDateFormat={globalDateFormat}
            />
          ) : (
            <CertsConsultedContent
              isCommentPopoverOpen={isSidePanelCommentDrawerOpen}
              setIsCommentPopoverOpen={setIsSidePanelCommentDrawerOpen}
              onClose={() => {
                setIsSidePanelCommentDrawerOpen(false);
              }}
              resetAction={onSubmit}
              certKey={certKey}
              nodeData={nodeData}
              globalDateFormat={globalDateFormat}
            />
          )}
        </Box>
      );
    }

    // TODO: Transitions broken, need data to be completely loaded before the drawer actually opens
    if (showConsultedLineItemMsg && !showConsultedActionedInfo) {
      return (
        <Box ref={contentRef} className='CertsSidePanelCommentDrawer-content'>
          {isMobileXlargeWidth ? (
            <CertsConsultPopover
              isMobileXlargeWidth={isMobileXlargeWidth}
              actionKind={drawerActionKind}
              isCommentPopoverOpen={isSidePanelCommentDrawerOpen}
              setIsCommentPopoverOpen={setIsSidePanelCommentDrawerOpen}
              placement={tooltipPosition}
              onSubmit={(checkBoxSelected) => {
                setIsSidePanelCommentDrawerOpen(false);
                setTimeout(() => {
                  closeConsultPopover(checkBoxSelected);
                }, 100);
                dispatch({ type: SET_IS_REASSIGN_POPOVER_MSG });
              }}
            />
          ) : (
            <CertsConsultContent
              onSubmit={(checkBoxSelected) => {
                setIsSidePanelCommentDrawerOpen(false);
                setTimeout(() => {
                  closeConsultPopover(checkBoxSelected);
                }, 100);
                dispatch({ type: SET_IS_REASSIGN_POPOVER_MSG });
              }}
              setIsCommentPopoverOpen={() =>
                setIsSidePanelCommentDrawerOpen(false)
              }
            />
          )}
        </Box>
      );
    }

    const getTitleString = () => {
      if (isWorksForMe && !isEOCampaign)
        return `${CONFIRM_MANAGER_TITLE_TEXT} ${getTypeActEntRole}?`;
      if (isApprove || isBelongsToMe || (isEOCampaign && isWorksForMe))
        return `${APPROVE_TITLE_TEXT} ${getTypeActEntRole}?`;
      if (isReject) return `${REJECT_ACTION_DATA.TITLE} ${getTypeActEntRole}?`;

      if (isDecommission) return `${DECOMMISSION_TEXT} ${getTypeActEntRole}?`;

      return `${APPROVE_TITLE_TEXT} ${getTypeActEntRole}?`;
    };

    if (shouldGetDrawerCommentForm) {
      if (isMobileXlargeWidth) {
        return isWorksForMe || isBelongsToMe || isApprove ? (
          <CertsActionPopover
            actionKind={drawerActionKind}
            isMobileXlargeWidth={isMobileXlargeWidth}
            isCommentPopoverOpen={
              isWorksForMe || isBelongsToMe || isApprove || isDecommission
                ? isSidePanelCommentDrawerOpen
                : false
            }
            setIsCommentPopoverOpen={
              drawerActionSelected ? () => {} : setIsSidePanelCommentDrawerOpen
            }
            placement={tooltipPosition}
            onSubmit={onSubmit}
            getTypeActEntRole={getTypeActEntRole}
          />
        ) : (
          <CertsRejectAction
            commentStatus={isRejectCommentRequired}
            isMobileXlargeWidth={isMobileXlargeWidth}
            handleClick={onSubmit}
            getTypeActEntRole={getTypeActEntRole}
            placement={tooltipPosition}
            isRejectPopoverOpen={isSidePanelCommentDrawerOpen}
            setIsRejectPopoverOpen={
              drawerActionSelected ? () => {} : setIsSidePanelCommentDrawerOpen
            }
          />
        );
      }

      const getKeyMatch = () => {
        // SA campaign Belong to me for account
        if (
          isSACampaign &&
          drawerActionKind === ACTION_BUTTON_KINDS.APPROVE &&
          getTypeActEntRole === CERT_TILE_TYPE.ACCOUNT
        ) {
          return PREDEFINED_COMMENTS_FOR_ACTION.BELONGS_TO_ME;
        }

        if (
          isEOCampaign &&
          drawerActionKind === ACTION_BUTTON_KINDS.APPROVE &&
          getTypeActEntRole === CERT_TILE_TYPE.ENTITLEMENT
        ) {
          return PREDEFINED_COMMENTS_FOR_ACTION.BELONGS_TO_ME;
        }

        if (drawerActionKind === ACTION_BUTTON_KINDS.APPROVE)
          return PREDEFINED_COMMENTS_FOR_ACTION.APPROVE;
        if (drawerActionKind === ACTION_BUTTON_KINDS.REJECT)
          return PREDEFINED_COMMENTS_FOR_ACTION.REJECT;
        if (
          drawerActionKind === ACTION_BUTTON_KINDS.WORKS_FOR_ME &&
          !isEOCampaign
        )
          return PREDEFINED_COMMENTS_FOR_ACTION.WORKS_FOR_ME;
        if (
          drawerActionKind === ACTION_BUTTON_KINDS.WORKS_FOR_ME &&
          isEOCampaign
        )
          return PREDEFINED_COMMENTS_FOR_ACTION.BELONGS_TO_ME;
        if (
          drawerActionKind === ACTION_BUTTON_KINDS.DECOMMISSION &&
          isEOCampaign
        )
          return PREDEFINED_COMMENTS_FOR_ACTION.DECOMMISSION;

        return null;
      };

      return (
        <Box ref={contentRef} className='CertsSidePanelCommentDrawer-content'>
          <Box className='CertsSidePanelCommentDrawer-header'>
            <div className='CertsSidePanelCommentDrawer-title'>
              <Icon
                kind='HelpFilled'
                className='CertsSidePanelCommentDrawer-headerIcon'
              />
              <Typography kind='label' desktop='h3-d' mobile='h1-m'>
                {getTitleString()}
              </Typography>
            </div>
            <ButtonIcon
              size='small'
              kind='ghost'
              tabIndex={-1}
              icon={<Icon kind='Close' />}
              onClick={toggleDrawerOpen}
            />
          </Box>
          <Box className='CertsSidePanelCommentDrawer-commentAndBtns'>
            <CertsModalCommentBox
              defaultComments={predefinedComments[getKeyMatch()]}
              showAlert={isCritical}
              onChange={setCommentValue}
              label={
                messages['comments.ATTESTATIONS'] ||
                localize(intl, getMessageKey('comments.ATTESTATIONS'))
              }
              size='medium'
              allowEditPredefinedComments={
                predefinedComments?.allowEditPredefinedComments
              }
            />
            <Box tag='div' className='CertsSidePanelCommentDrawer-controls'>
              <Button type='button' kind='ghost' onClick={toggleDrawerOpen}>
                {CANCEL_BUTTON_TEXT}
              </Button>
              <Button
                type='button'
                kind={!isCommentNotValid ? 'filled' : 'outlined'}
                onClick={() => handleCommentSubmit()}>
                {getCommentSubmitLabel(
                  drawerActionKind,
                  messages,
                  intl,
                  isEOCampaign
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      );
    }

    if (isReassignUserOrParent) {
      return (
        <Box ref={contentRef} className='CertsSidePanelCommentDrawer-content'>
          <Box className='CertsSidePanelCommentDrawer-contentAndBtns'>
            <div className='CertsSidePanelCommentDrawer-reassignCloseBtn'>
              <ButtonIcon
                size='small'
                kind='ghost'
                tabIndex={-1}
                icon={<Icon kind='Close' />}
                onClick={toggleDrawerOpen}
              />
            </div>
            <Typography
              kind='body2'
              mobile='body2-d'
              className='CertsSidePanelCommentDrawer-reassignText'>
              {l(
                'By reassigning this {lineItem}, their associated items will be included. Any approvals or rejections will be cleared upon reassignment.',
                {
                  lineItem: l(getTypeActEntRole).toLocaleLowerCase(),
                }
              )}
            </Typography>

            <Box tag='div' className='CertsSidePanelCommentDrawer-controls'>
              <Button type='button' kind='ghost' onClick={toggleDrawerOpen}>
                {l('Cancel')}
              </Button>
              <Button
                type='button'
                kind='filled'
                onClick={() => {
                  actionBtnRefs?.current[
                    drawerActionKind
                  ].current?.handleReassignUserPopover();
                  setIsSidePanelCommentDrawerOpen(false);
                }}>
                {l('Got it')}
              </Button>
            </Box>
          </Box>
        </Box>
      );
    }

    return null;
  };

  if (!isSidePanelCommentDrawerOpen) return null;

  const drawerContent = getDrawerContent();

  return (
    <Box tag='div' ref={drawerRef} className={sidePanelCommentDrawerClasses}>
      {drawerContent}
    </Box>
  );
}

CertsSidePanelCommentDrawer.propTypes = {
  isSidePanelCommentDrawerOpen: PropTypes.bool.isRequired,
  setIsSidePanelCommentDrawerOpen: PropTypes.func.isRequired,
  drawerActionKind: PropTypes.oneOf(Object.values(ACTION_BUTTON_KINDS)),
  onSubmit: PropTypes.func.isRequired,
  getTypeActEntRole: PropTypes.string.isRequired,
  isRejectCommentRequired: PropTypes.bool.isRequired,
  setIsSidePanelLoadingKind: PropTypes.func,
  nodeData: PropTypes.shape(NODE_DATA_MODEL).isRequired,
  certKey: PropTypes.string.isRequired,
  isSaviResponded: PropTypes.bool.isRequired,
  globalDateFormat: PropTypes.string.isRequired,
  drawerActionSelected: PropTypes.bool.isRequired,
  consulteeLoggedIn: PropTypes.bool.isRequired,
  closeConsultPopover: PropTypes.func.isRequired,
  actionBtnRefs: PropTypes.arrayOf(
    PropTypes.shape({ current: PropTypes.shape({}) })
  ).isRequired,
  tooltipPosition: PropTypes.string.isRequired,
  isMobileXlargeWidth: PropTypes.bool.isRequired,
  activeTab: PropTypes.string,
  isActionDrawerOutsideClick: PropTypes.bool.isRequired,
  setIsActionDrawerOutsideClick: PropTypes.func.isRequired,
};

CertsSidePanelCommentDrawer.defaultProps = {
  drawerActionKind: null,
  setIsSidePanelLoadingKind: () => {},
  activeTab: TAB_STATUS_DONE,
};

export default CertsSidePanelCommentDrawer;
